import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  VIEW_PRESET_PUT_CREATE,
  VIEW_PRESET_DELETE_DELETE_BY_ID,
  VIEW_PRESET_PATCH_SET_FLAGS,
  VIEW_PRESET_POST_GET_BY_CONTEXT_PROPERTIES,
  VIEW_PRESET_GET_GET_BY_VIEW_ID,
  VIEW_PRESET_POST_UPDATE_BY_ID,
  VIEW_PRESET_POST_ADMIN_COPY,
  VIEW_PRESET_POST_ADMIN_LIST,
  VIEW_PRESET_POST_ADMIN_UPDATE,
  VIEW_PRESET_PATCH_ADMIN_SET_FLAGS,
  VIEW_PRESET_DELETE_ADMIN_DELETE,
  VIEW_PRESET_PUT_ADMIN_CREATE,
} from '../endpoints/view-preset.endpoints';
import { IServerSideGetRowsRequest, LoadSuccessParams } from 'ag-grid-community';
import { HttpResponse } from '@angular/common/http';
import { map, tap } from 'rxjs';
import {
  CompanyRef,
  DataObjectRef,
  UserRef,
} from 'src/app/core/models/object-ref.models';
import { UtilsService } from 'src/app/core/services/utils.service';
import { toQueryParams } from '../api-utils';
import { ViewId } from '../generated/view-id.generated';

import { Observable } from 'rxjs';
import { NomItem, TimeInfo } from 'src/app/core/models/core.models';

export interface ViewPreset {
  id?: number;
  uuid?: string;
  view_id: string;
  context_object_id: string;
  context_object_class_id: number;
  name: string;
  description?: string;
  settings?: any | unknown;
  isCreateNew?: boolean;
  is_pinned?: boolean;
  is_default?: boolean;
  /** `true` on System and Company rows: the caller may read them, never write them. */
  is_locked?: boolean;
  /** Owner scope, a `NomViewPresetScope` id: System 10, Company 30, User 40. */
  view_preset_scope_id?: number;
  scope?: NomItem;
  /** Owner company; the zero uuid on System rows. */
  company_id?: string;
  /**
   * `true` on the ONE row of a `get_by_view_id` list that opens the view: the default
   * row with the highest scope (Mine over Company over System). Always `false` on a
   * single-item response, so cache patches re-derive it (`withEffectiveDefault`).
   */
  is_effective_default?: boolean;
  time_created?: TimeInfo;
  time_modified?: TimeInfo;
}

/**
 * One row of Admin -> View Presets (POST /view_preset/admin_list, SEN-5223): the
 * System and Company presets the caller may manage, with the canonical refs the
 * grid renders and deep-links through.
 */
export interface AdminViewPreset extends ViewPreset {
  /** Owner company; null on System rows. */
  company: CompanyRef | null;
  created_by_user: UserRef | null;
  /** Who last changed the row (SEN-5275). */
  modified_by_user: UserRef | null;
  /** The object the preset is bound to; null when it applies to every object of the view. */
  context_object: DataObjectRef | null;
}

/** Body of POST /view_preset/admin_update: the row plus the fields to change. */
export interface AdminViewPresetUpdate {
  uuid: string;
  name?: string;
  description?: string;
}

/**
 * Body of POST /view_preset/admin_copy. Same-scope, same-company copies default
 * to the source name suffixed " (copy)"; another `company_id` is Global Admin only.
 */
export interface AdminViewPresetCopy {
  uuid: string;
  view_preset_scope_id: number;
  company_id?: string;
  name?: string;
}

/** Body of PATCH /view_preset/set_flags/{uuid}: only the flags to change. */
export type ViewPresetFlags = Partial<
  Pick<ViewPreset, 'is_default' | 'is_pinned'>
>;

/**
 * `ViewId` is generated from the backend nomenclature `nom_view_preset_view`
 * (`./sensika nomenclature:export-view-ids`, SEN-5223 / WAP-1638) and re-exported
 * here so every existing import keeps working. Register a new view in the
 * nomenclature first, regenerate, then use it - an unregistered id fails `tsc`.
 */
export {
  ViewId,
  ViewType,
  VIEW_TYPE_BY_VIEW_ID,
  NAMED_PRESET_VIEW_TYPES,
} from '../generated/view-id.generated';

/**
 * Identity of one view preset: the triple
 * POST /view_preset/get_by_context_properties is keyed on.
 */
export interface ViewPresetContext {
  viewId: ViewId;
  contextObjectId: string;
  contextObjectClassId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ViewPresetApiService {
  private readonly http = inject(HttpClient);
  private readonly utils = inject(UtilsService);

  /**
   * The request body is a partial `ViewPreset` - the backend matches on the context
   * triple and ignores the rest. `name: ''` is what every caller sends.
   */
  getViewPresetByContext(context: ViewPresetContext): Observable<ViewPreset> {
    return this.http.post<ViewPreset>(
      VIEW_PRESET_POST_GET_BY_CONTEXT_PROPERTIES,
      {
        view_id: context.viewId,
        context_object_id: context.contextObjectId,
        context_object_class_id: context.contextObjectClassId,
        name: '',
      },
    );
  }

  /**
   * The ag-grid column-preset front door (`app-data-grid`), together with
   * `loadAvalableViewPresets`; per-context presets go through `getViewPresetByContext`.
   * Create (PUT) vs update (POST) branches on `saveAsNew`. Every view-preset caller
   * passes `false`; `true` is the ag-grid "save as new view" path.
   */
  saveViewPreset(
    viewPreset: ViewPreset,
    saveAsNew: boolean = false,
  ): Observable<ViewPreset> {
    return saveAsNew
      ? this.http.put<ViewPreset>(VIEW_PRESET_PUT_CREATE, viewPreset)
      : this.http.post<ViewPreset>(VIEW_PRESET_POST_UPDATE_BY_ID, viewPreset);
  }

  loadAvalableViewPresets(viewId: ViewId): Observable<ViewPreset[]> {
    return this.http.get<ViewPreset[]>(VIEW_PRESET_GET_GET_BY_VIEW_ID, {
      params: toQueryParams({ view_id: viewId }),
    });
  }

  /**
   * Flip `is_default` / `is_pinned` on one grid preset without resending its
   * settings; the backend keeps one default per user + company + view. Backs the
   * toolbar's star (and the coming pin control / unpinnable tabs).
   */
  setViewPresetFlags(
    viewPresetId: string,
    flags: ViewPresetFlags,
  ): Observable<ViewPreset> {
    return this.http.patch<ViewPreset>(
      VIEW_PRESET_PATCH_SET_FLAGS(viewPresetId),
      flags,
    );
  }

  deleteSettings(viewPresetId: string): Observable<unknown> {
    return this.http.delete(VIEW_PRESET_DELETE_DELETE_BY_ID(viewPresetId));
  }

  /**
   * PUT /view_preset/admin_create: a shared preset (`view_preset_scope_id` System 10
   * or Company 30). A Company row defaults to the caller's own company; without a
   * context pair the row applies to every object of the view. 403 below the
   * scope's admin level (Client Admin+ for Company, Platform Admin+ for System).
   */
  adminCreateViewPreset(viewPreset: ViewPreset): Observable<ViewPreset> {
    return this.http.put<ViewPreset>(VIEW_PRESET_PUT_ADMIN_CREATE, viewPreset);
  }

  /**
   * POST /view_preset/admin_copy: copies any row the caller can reach (own, Company
   * or System) into the System or Company scope as a new, unflagged row - the
   * personal "promote" and the admin grid's copy are the same call. A context bound
   * to the source's user or to another company is dropped. 404 when the source is
   * out of reach, 400 when the name already exists in the target scope.
   */
  adminCopyViewPreset(copy: AdminViewPresetCopy): Observable<AdminViewPreset> {
    return this.http.post<AdminViewPreset>(VIEW_PRESET_POST_ADMIN_COPY, copy);
  }

  /**
   * POST /view_preset/admin_list[?company_id=...] - the Solr-backed admin grid: the
   * Company presets of the active company plus every System row. `companyId` is
   * the Global Admin's switch; omitted = the caller's active company.
   */
  getAdminViewPresetsAgGridModel(
    gridParams: IServerSideGetRowsRequest,
    companyId?: string,
  ): Observable<LoadSuccessParams> {
    return this.http.post<LoadSuccessParams>(
      VIEW_PRESET_POST_ADMIN_LIST,
      gridParams,
      { params: toQueryParams({ company_id: companyId || undefined }) },
    );
  }

  /** POST /view_preset/admin_list?output=csv - see ClientAdminApiService.exportUsersCsv(). */
  exportAdminViewPresetsCsv(
    request: IServerSideGetRowsRequest,
    companyId?: string,
  ): Observable<void> {
    return this.http
      .post(VIEW_PRESET_POST_ADMIN_LIST, request, {
        params: toQueryParams({
          output: 'csv',
          company_id: companyId || undefined,
        }),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const fileName = this.utils.getFileNameFromDisposition(
            response.headers.get('content-disposition'),
            'view-presets.csv',
          );
          this.utils.downloadBlob(response.body!, fileName);
        }),
        map(() => undefined),
      );
  }

  /** POST /view_preset/admin_update - rename / describe a shared row (guarded property map). */
  adminUpdateViewPreset(
    update: AdminViewPresetUpdate,
  ): Observable<AdminViewPreset> {
    return this.http.post<AdminViewPreset>(
      VIEW_PRESET_POST_ADMIN_UPDATE,
      update,
    );
  }

  /** PATCH /view_preset/admin_set_flags/{uuid} - `is_default` / `is_pinned` on a shared row. */
  adminSetViewPresetFlags(
    uuid: string,
    flags: ViewPresetFlags,
  ): Observable<AdminViewPreset> {
    return this.http.patch<AdminViewPreset>(
      VIEW_PRESET_PATCH_ADMIN_SET_FLAGS(uuid),
      flags,
    );
  }

  /** DELETE /view_preset/admin_delete/{uuid} - deletes one shared row. */
  adminDeleteViewPreset(uuid: string): Observable<unknown> {
    return this.http.delete(VIEW_PRESET_DELETE_ADMIN_DELETE(uuid));
  }
}
